import React from 'react';
import css from './AdvancedFooter.module.scss';
import type { BasilPlanRow } from '../types/basilTypes';
import type { Projection } from '../../../../dorian-shared/types/episodeScript';

/**
 * P6: the link that opens it, out of the writing area and down in the footer.
 *
 * "Under the hood" invited a writer to look inside; this says who it is for. Nothing behind it has
 * changed, and none of the checks it carries has been removed — it is the same drawer, in a place
 * that does not compete with the script for the writer's attention.
 */
export const DETAILS_FOR_SUPPORT = 'Details for support';

/** The most rows the footer lists before it says how many more there are (editor.js:367). */
export const ADVANCED_ROWS = 40;

/** What the footer needs. Every engine word on the surface lives here and only here. */
export type AdvancedFooterProps = {
  /** The staged changes, in the writer's words and the engine's. */
  rows: BasilPlanRow[];
  /** The raw request bodies, exactly as they would be sent. */
  raw: unknown;
  /** The projection lines and digest — machine text, never the writer's view. */
  projection: Projection | null;
};

/**
 * The collapsed `details` at the foot of the modal. It carries the engine's own spelling of every
 * change, the raw request bodies and the projection digest — and nothing above it does.
 */
export function AdvancedFooter(props: AdvancedFooterProps) {
  const { rows, raw, projection } = props;
  const shown = rows.slice(0, ADVANCED_ROWS);
  const more = rows.length - shown.length;

  return (
    <details className={css.details} data-testid="basil-advanced">
      <summary className={css.summary}>{DETAILS_FOR_SUPPORT}</summary>
      <div className={css.rows}>
        {shown.map((row) => (
          <div className={css.row} key={row.key}>
            <span>{row.phrase}</span>
            <span className={css.raw}>{row.engineWords}</span>
          </div>
        ))}
        {more > 0 ? (
          <div className={css.more}>
            {`+${more} more, all in the text below`}
          </div>
        ) : null}
      </div>
      {projection ? (
        <pre className={css.raw} data-testid="basil-projection-raw">
          {`projection v${projection.version} · ${projection.branchCount} nodes · digest ${projection.digest.join(':')}\n${projection.lines.join('\n')}`}
        </pre>
      ) : null}
      <pre className={css.raw} data-testid="basil-raw-bodies">
        {rows.length > 0 ? JSON.stringify(raw, null, 1) : ''}
      </pre>
    </details>
  );
}
